import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ClipboardList, UserPlus } from 'lucide-react';
import ReferralDetailDrawer from '../../components/agent/ReferralDetailDrawer';
import { ClientStageBadge, LeadStageBadge } from '../../components/agent/AgentStageBadge';
import StatusCellWithTooltip from '../../components/clients/StatusCellWithTooltip';
import {
  DEFAULT_PAGE_SIZE,
  EmptyState,
  ErrorState,
  HeaderRow,
  LinkButton,
  LoadingHint,
  LoadingState,
  Muted,
  PAGE_SIZE_OPTIONS,
  PageHeader,
  PageSubtitle,
  PageTitle,
  Pager,
  Panel,
  PortalPage,
  PrimaryLink,
  SearchInput,
  Select,
  Tab,
  Table,
  TableWrap,
  Tabs,
  Td,
  Th,
  Toolbar,
  Tr,
} from '../../components/agent/AgentPortalUI';
import {
  agentPortalApi,
  AgentClientRow,
  AgentCommittee,
  AgentLeadRow,
  AgentPaged,
  CLIENT_STAGE_OPTIONS,
  formatDateTime,
  getCommitteeDecisionLabel,
  LEAD_STAGE_OPTIONS,
} from '../../services/agentPortalService';
import { formatDate } from '../../services/agentBalancesService';
import { getBenefitTypeName } from '../../services/leadsService';
import { extractErrorMessage } from '../../utils/errorHandler';

type TabKey = 'clients' | 'leads';

const SEARCH_DEBOUNCE_MS = 300;

const parsePositiveInt = (value: string | null): number | undefined => {
  const parsed = value ? parseInt(value, 10) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const benefitText = (benefitType?: number | null, other?: string | null) => {
  if (!benefitType) return <Muted>-</Muted>;
  const name = getBenefitTypeName(benefitType);
  return other ? `${name} (${other})` : name;
};

const lastDecisionText = (decision?: AgentCommittee | null) => {
  if (!decision) return <Muted>-</Muted>;
  const percentage = decision.disabilityPercentage != null ? ` · ${decision.disabilityPercentage}%` : '';
  return (
    <span title={formatDate(decision.committeeDate)}>
      {getCommitteeDecisionLabel(decision.decisionStatus)}
      {percentage}
    </span>
  );
};

const AgentReferralsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // מצב הסינון — מה-URL בלבד (ריענון/שיתוף/Back שומרים אותו). ברירות מחדל לא נכתבות ל-URL.
  const tab: TabKey = searchParams.get('tab') === 'leads' ? 'leads' : 'clients';
  const search = searchParams.get('q') ?? '';
  const stage = parsePositiveInt(searchParams.get('stage'));
  const page = parsePositiveInt(searchParams.get('page')) ?? 1;
  const requestedPageSize = parsePositiveInt(searchParams.get('pageSize'));
  const pageSize =
    requestedPageSize && PAGE_SIZE_OPTIONS.includes(requestedPageSize) ? requestedPageSize : DEFAULT_PAGE_SIZE;

  const updateParams = useCallback(
    (updates: Record<string, string | number | undefined>, options: { resetPage?: boolean; replace?: boolean } = {}) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            if (value === undefined || value === '') next.delete(key);
            else next.set(key, String(value));
          });
          if (options.resetPage ?? true) next.delete('page');
          return next;
        },
        { replace: options.replace }
      );
    },
    [setSearchParams]
  );

  // חיפוש עם debounce — השרת מקבל רק את הערך הסופי
  const [searchInput, setSearchInput] = useState(search);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setSearchInput(search); // סנכרון כשה-URL משתנה מבחוץ (Back/Forward)
  }, [search]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateParams({ q: value.trim() || undefined }, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  // נתונים
  const [clientsData, setClientsData] = useState<AgentPaged<AgentClientRow> | null>(null);
  const [leadsData, setLeadsData] = useState<AgentPaged<AgentLeadRow> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  useEffect(() => {
    // ביטול הבקשה הקודמת — תשובה ישנה לא תדרוס תוצאה חדשה
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    const filter = { page, pageSize, search: search || undefined, stage };
    const request =
      tab === 'clients'
        ? agentPortalApi.getClients(filter, controller.signal).then(setClientsData)
        : agentPortalApi.getLeads(filter, controller.signal).then(setLeadsData);

    request
      .catch(err => {
        if (axios.isCancel(err)) return;
        setError(extractErrorMessage(err, tab === 'clients' ? 'שגיאה בטעינת הלקוחות' : 'שגיאה בטעינת הלידים'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [tab, page, pageSize, search, stage, reloadKey]);

  const handleTabChange = (next: TabKey) => {
    if (next === tab) return;
    // ערכי השלב שונים בין הלשוניות — מאפסים שלב ועמוד, החיפוש נשמר
    updateParams({ tab: next === 'clients' ? undefined : next, stage: undefined });
  };

  const handleClear = () => {
    setSearchInput('');
    updateParams({ q: undefined, stage: undefined });
  };

  const stageOptions = tab === 'clients' ? CLIENT_STAGE_OPTIONS : LEAD_STAGE_OPTIONS;
  const data = tab === 'clients' ? clientsData : leadsData;
  const hasFilters = !!search || stage !== undefined;
  const isFirstLoad = loading && !data;

  const renderClients = (rows: AgentClientRow[]) => (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th>שם</Th>
            <Th>סוג קצבה</Th>
            <Th>תאריך חתימה</Th>
            <Th>שלב</Th>
            <Th>סטטוס</Th>
            <Th>ועדה צפויה</Th>
            <Th>החלטה אחרונה</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <Tr
              key={row.id}
              $clickable
              tabIndex={0}
              onClick={() => setSelectedClientId(row.id)}
              onKeyDown={e => e.key === 'Enter' && setSelectedClientId(row.id)}
            >
              <Td>
                <strong>{row.fullName}</strong>
              </Td>
              <Td>{benefitText(row.benefitType)}</Td>
              <Td>{formatDate(row.signedAt)}</Td>
              <Td>
                <ClientStageBadge stage={row.stage} />
              </Td>
              <Td>
                <StatusCellWithTooltip
                  align="start"
                  currentStatusName={row.statusName}
                  currentStatusColor={row.statusColor}
                  currentStages={row.currentStages}
                />
              </Td>
              <Td>{row.upcomingCommittee ? formatDateTime(row.upcomingCommittee.committeeDate) : <Muted>-</Muted>}</Td>
              <Td>{lastDecisionText(row.lastDecision)}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );

  const renderLeads = (rows: AgentLeadRow[]) => (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th>שם</Th>
            <Th>סוג קצבה</Th>
            <Th>שלב</Th>
            <Th>סטטוס</Th>
            <Th>תאריך הפניה</Th>
            <Th>עדכון אחרון</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <Tr key={row.id}>
              <Td>
                <strong>{row.fullName}</strong>
              </Td>
              <Td>{benefitText(row.benefitType, row.otherBenefitDescription)}</Td>
              <Td>
                <LeadStageBadge stage={row.stage} />
              </Td>
              <Td>
                <StatusCellWithTooltip align="start" currentStatusName={row.statusName} currentStatusColor={row.statusColor} />
              </Td>
              <Td>{formatDate(row.referredAt)}</Td>
              <Td>{formatDate(row.lastUpdatedAt)}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );

  const renderContent = () => {
    if (error) return <ErrorState text={error} onRetry={() => setReloadKey(k => k + 1)} />;
    if (isFirstLoad || !data) return <LoadingState />;
    if (data.items.length === 0) {
      if (hasFilters) return <EmptyState text="לא נמצאו תוצאות לסינון הנוכחי" />;
      return <EmptyState text={tab === 'clients' ? 'עדיין אין לקוחות שחתמו' : 'אין לידים בטיפול'} />;
    }

    return (
      <>
        {tab === 'clients' ? renderClients(clientsData?.items ?? []) : renderLeads(leadsData?.items ?? [])}
        <Pager
          page={page}
          pageSize={pageSize}
          totalCount={data.totalCount}
          onPageChange={p => updateParams({ page: p > 1 ? p : undefined }, { resetPage: false })}
          onPageSizeChange={size => updateParams({ pageSize: size === DEFAULT_PAGE_SIZE ? undefined : size })}
        />
      </>
    );
  };

  return (
    <PortalPage>
      <PageHeader>
        <HeaderRow>
          <div>
            <PageTitle>
              <ClipboardList size={26} />
              ההפניות שלי
            </PageTitle>
            <PageSubtitle>הלקוחות והלידים שהפנית, והסטטוס והשלב של כל תיק</PageSubtitle>
          </div>
          <PrimaryLink to="/agent/leads/new">
            <UserPlus size={18} />
            ליד חדש
          </PrimaryLink>
        </HeaderRow>
      </PageHeader>

      <Panel>
        <Tabs role="tablist">
          <Tab type="button" role="tab" aria-selected={tab === 'clients'} $active={tab === 'clients'} onClick={() => handleTabChange('clients')}>
            לקוחות
          </Tab>
          <Tab type="button" role="tab" aria-selected={tab === 'leads'} $active={tab === 'leads'} onClick={() => handleTabChange('leads')}>
            לידים
          </Tab>
        </Tabs>

        <Toolbar>
          <SearchInput
            type="search"
            placeholder="חיפוש לפי שם"
            aria-label="חיפוש לפי שם"
            value={searchInput}
            onChange={e => handleSearchChange(e.target.value)}
          />
          <Select
            aria-label="סינון לפי שלב"
            value={stage ?? ''}
            onChange={e => updateParams({ stage: e.target.value || undefined })}
          >
            <option value="">כל השלבים</option>
            {stageOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          {hasFilters && (
            <LinkButton type="button" onClick={handleClear}>
              נקה סינון
            </LinkButton>
          )}
          {loading && !isFirstLoad && <LoadingHint>מעדכן...</LoadingHint>}
        </Toolbar>

        {renderContent()}
      </Panel>

      {selectedClientId != null && (
        <ReferralDetailDrawer clientId={selectedClientId} onClose={() => setSelectedClientId(null)} />
      )}
    </PortalPage>
  );
};

export default AgentReferralsPage;
